import { Link, useLocation, useNavigate } from 'react-router-dom';
import { initials, type AdminCompanyRow, type CompanyStatus, type Tone } from '@coachrex/shared';
import { useAdminCompanies, useOpenCompany } from '../lib/api';
import { Chip, ErrorBox, Skeleton } from '../components/ui';

const STATUS: Record<CompanyStatus, { label: string; tone: Tone }> = {
  connected: { label: 'Connected', tone: 'green' },
  importing: { label: 'Importing', tone: 'amber' },
  token_invalid: { label: 'Token invalid', tone: 'red' },
  disconnected: { label: 'Disconnected', tone: 'grey' },
};

export function Companies() {
  const { data, error, isLoading } = useAdminCompanies();
  const open = useOpenCompany();
  const navigate = useNavigate();
  const added = (useLocation().state as { added?: string } | null)?.added;

  const openCompany = (c: AdminCompanyRow) => {
    if (c.status === 'token_invalid') return navigate(`/admin/companies/${c.id}/fix`);
    open.mutate(c.id, { onSuccess: () => navigate('/conversations') });
  };

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <div className="page-head">
        <div className="page-head__titles">
          <span className="overline" style={{ fontSize: 11.5 }}>
            App owner
          </span>
          <h1 className="h1">Companies</h1>
          <span className="subtitle">Every business using Coach Lex. Each one has its own HighLevel connection, settings, scoring rules and managers.</span>
        </div>
        <Link to="/admin/new" className="btn btn--primary">
          Add company
        </Link>
      </div>

      {added && (
        <div className="test-ok" role="status">
          <span className="test-ok__title">{added} added</span>
          <span style={{ fontSize: 12.5, color: 'var(--body)' }}>Coach Lex is importing the last 90 days of calls, texts, emails and tags. This usually takes 10–30 minutes.</span>
        </div>
      )}

      <section className="card card--list">
        <div className="list-head co-grid">
          <span>Company</span>
          <span>HighLevel</span>
          <span style={{ textAlign: 'right' }}>Managers</span>
          <span style={{ textAlign: 'right' }}>Leads, 90d</span>
          <span style={{ textAlign: 'right' }}>Last sync</span>
          <span />
        </div>
        {error ? (
          <div style={{ padding: 12 }}>
            <ErrorBox error={error} />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton height={44} />
            <Skeleton height={44} />
          </div>
        ) : !data?.length ? (
          <div className="list-empty">
            No companies yet. <Link to="/admin/new">Add the first one</Link> to start importing from HighLevel.
          </div>
        ) : (
          data.map((c) => {
            const s = STATUS[c.status];
            const label = c.status === 'token_invalid' ? 'Fix connection' : c.current ? 'Current' : 'Open';
            return (
              <div key={c.id} className={`list-row co-grid co-row${c.current ? ' is-current' : ''}`}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span className="co-tile">{initials(c.name).slice(0, 2)}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Link to={`/admin/companies/${c.id}`} style={{ fontWeight: 600, color: 'var(--ink)' }}>
                      {c.name}
                    </Link>
                    <span style={{ fontSize: 12, color: 'var(--faint)' }}>{c.industry ?? '—'}</span>
                  </span>
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                  <Chip tone={s.tone}>{c.importPct !== null ? `Importing · ${c.importPct}%` : s.label}</Chip>
                  <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>Location {c.locationMasked}</span>
                </span>
                <span className="num">{c.managers}</span>
                <span className="num">{c.leads90d.toLocaleString('en-US')}</span>
                <span className="num" style={{ fontSize: 12.5, color: 'var(--faint)' }}>
                  {c.lastSyncAt ? ago(c.lastSyncAt) : 'Not yet'}
                </span>
                <span style={{ justifySelf: 'end', display: 'flex', gap: 6 }}>
                  <Link className="btn btn--secondary btn--sm" to={`/admin/companies/${c.id}`}>
                    Settings
                  </Link>
                  <button className="btn btn--secondary btn--sm" disabled={c.current && c.status !== 'token_invalid'} onClick={() => openCompany(c)}>
                    {label}
                  </button>
                </span>
              </div>
            );
          })
        )}
      </section>
      <span className="footnote">Only app owners can open Admin. Managers only see the company they belong to.</span>
    </div>
  );
}

function ago(iso: string): string {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}
